<template>
  <el-dialog
    v-model="dialogVisible"
    title="编辑个人资料"
    width="500px"
    :close-on-click-modal="false"
    :before-close="handleClose"
  >
    <div v-if="loading" class="loading">加载中...</div>
    <el-form 
      v-else 
      :model="profileForm" 
      :rules="rules" 
      ref="formRef"
      label-width="80px"
      style="padding: 10px 0;"
    >
      <el-form-item label="用户名" prop="name">
        <el-input 
          v-model="profileForm.name" 
          placeholder="请输入用户名"
          maxlength="20"
          show-word-limit
        />
      </el-form-item>
      
      <el-form-item label="邮箱" prop="email">
        <el-input 
          v-model="profileForm.email" 
          placeholder="请输入邮箱"
        />
      </el-form-item>
      
      <el-form-item label="电话" prop="phone">
        <el-input 
          v-model="profileForm.phone" 
          placeholder="请输入电话号码"
        />
      </el-form-item>
      
      <el-form-item label="个人介绍" prop="signature">
        <el-input 
          v-model="profileForm.signature" 
          type="textarea"
          :rows="4"
          placeholder="请输入个人介绍"
          maxlength="100"
          show-word-limit
        />
      </el-form-item>
    </el-form>
    
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button 
          type="primary" 
          @click="submitForm" 
          :loading="submitting"
        >
          {{ submitting ? '保存中...' : '保存' }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, defineProps, defineEmits } from 'vue';
import { ElMessage, ElForm, ElFormItem, ElInput, ElDialog, ElButton } from 'element-plus';
import { getUserProfile, updateUserProfile } from '@/user/api/userApi';
import { useStore } from 'vuex'; // 导入useStore

interface Props {
  modelValue: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'updated', profile: any): void;
  (e: 'close'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const store = useStore(); // 初始化store

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const formRef = ref<InstanceType<typeof ElForm>>();
const loading = ref(false);
const submitting = ref(false);
const profileForm = ref({
  name: '',
  email: '',
  phone: '',
  signature: ''
});

// 表单验证规则
const rules = {
  name: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 2, max: 20, message: '用户名长度应在2-20个字符之间', trigger: 'blur' }
  ],
  email: [
    { required: true, message: '请输入邮箱地址', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
  ],
  phone: [
    { required: true, message: '请输入电话号码', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
  ],
  signature: [
    { max: 100, message: '个人介绍不能超过100个字符', trigger: 'blur' }
  ]
};

// 加载用户资料
const loadUserProfile = async () => {
  try {
    loading.value = true;
    const response = await getUserProfile();
    
    if (response.data.code === 1) {
      const data = response.data.data;
      profileForm.value = {
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        signature: data.signature || ''
      };
    } else {
      throw new Error(response.data.msg || '获取用户资料失败');
    }
  } catch (error: any) {
    console.error('获取用户资料失败:', error);
    ElMessage.error(error.message || '获取用户资料失败');
  } finally {
    loading.value = false;
  }
};

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  // 验证表单
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) return;
  
  try {
    submitting.value = true;
    
    const response = await updateUserProfile(profileForm.value);
    
    if (response.data.code === 1) {
      ElMessage.success('个人资料更新成功！');
      
      // 更新成功后，获取最新的用户资料并更新store
      try {
        const profileResponse = await getUserProfile();
        
        if (profileResponse && profileResponse.data && profileResponse.data.code === 1) {
          // 更新用户信息到store
          store.commit('SET_USER_INFO', profileResponse.data.data);
          console.log('用户资料已更新到store');
        } else {
          console.error('获取用户资料失败:', profileResponse?.data?.msg || '未知错误');
        }
      } catch (error) {
        console.error('获取最新用户资料时发生错误:', error);
      }

      emit('updated', profileForm.value);
      handleClose();
    } else {
      throw new Error(response.data.msg || '更新失败');
    }
  } catch (error: any) {
    console.error('更新个人资料失败:', error);
    ElMessage.error(error.message || '更新个人资料失败');
  } finally {
    submitting.value = false;
  }
};

// 关闭对话框
const handleClose = () => {
  emit('close');
  nextTick(() => {
    if (formRef.value) {
      formRef.value.clearValidate();
    }
  });
};

// 监听弹窗显示状态变化

watch(dialogVisible, (newValue) => {
  if (newValue) {
    loadUserProfile();
  }
});
</script>

<style scoped>
.loading {
  text-align: center;
  padding: 20px;
  color: #8B7D6B;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>